import { forwardRef, useEffect, useRef, useCallback } from 'react';
import { Input, type InputProps } from '../Input/Input';
import { applyMask, MASK_PLACEHOLDERS, type MaskType } from '@/shared/utils/inputMasks';

export interface MaskedInputProps extends Omit<InputProps, 'type'> {
  maskType: MaskType;
}

/**
 * A drop-in replacement for `Input` that auto-formats the value according to
 * a Swiss field mask (AHV, IBAN, phone) on every keystroke.
 *
 * Compatible with React Hook Form's `register()` — just swap `Input` for
 * `MaskedInput` and add the `maskType` prop.
 *
 * Also formats the initial value on mount so that values loaded from the
 * backend (set programmatically by RHF via its ref callback) are displayed
 * in the correct format without requiring a keystroke.
 */
export const MaskedInput = forwardRef<HTMLInputElement, MaskedInputProps>(
  function MaskedInput({ maskType, onChange, onFocus, onKeyDown, placeholder, ...props }, forwardedRef) {
    const localRef = useRef<HTMLInputElement | null>(null);

    // Stable ref to latest onChange so the mount effect doesn't need it as a dep
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    // Merge our local ref with RHF's forwarded ref so both stay in sync
    const mergedRef = useCallback(
      (el: HTMLInputElement | null) => {
        localRef.current = el;
        if (typeof forwardedRef === 'function') {
          forwardedRef(el);
        } else if (forwardedRef) {
          (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
        }
      },
      [forwardedRef]
    );

    // RHF sets el.value synchronously in its ref callback (before effects run),
    // so by the time this effect fires the default value is already in the DOM.
    // Format it so that values loaded from the backend display correctly.
    useEffect(() => {
      const el = localRef.current;
      if (!el || !el.value) return;
      const formatted = applyMask(el.value, maskType);
      if (formatted === el.value) return;
      el.value = formatted;
      // Notify RHF so its internal state reflects the formatted value
      onChangeRef.current?.({ target: el, currentTarget: el } as React.ChangeEvent<HTMLInputElement>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // mount-only: format whatever RHF placed in the DOM via its ref callback

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      const formatted = applyMask(e.target.value, maskType);
      e.target.value = formatted;
      onChange?.(e);
    }

    function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
      if (maskType === 'ahv' && !e.target.value) {
        e.target.value = '756.';
        onChange?.({ ...e, target: e.target } as React.ChangeEvent<HTMLInputElement>);
      }
      if (maskType === 'phone_ch') {
        if (!e.target.value) {
          e.target.value = '+41 ';
          onChange?.({ ...e, target: e.target } as React.ChangeEvent<HTMLInputElement>);
        } else if (e.target.value.startsWith('+41 ')) {
          // The browser applies select-all after the focus event fires, so we defer
          // repositioning the cursor to after the +41 prefix via setTimeout.
          // This means typing replaces only the number portion, not the country code.
          const el = e.target;
          setTimeout(() => el.setSelectionRange(4, el.value.length), 0);
        }
      }
      onFocus?.(e);
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      // Prevent deleting past the "756." prefix in AHV fields
      if (maskType === 'ahv') {
        const input = e.currentTarget;
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        if (
          (e.key === 'Backspace' && start <= 4 && end <= 4) ||
          (e.key === 'Delete' && start < 4 && start === end)
        ) {
          e.preventDefault();
          return;
        }
      }
      // Prevent deleting past the "+41 " prefix in phone fields
      if (maskType === 'phone_ch' && e.currentTarget.value.startsWith('+41 ')) {
        const input = e.currentTarget;
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        if (
          (e.key === 'Backspace' && start <= 4 && end <= 4) ||
          (e.key === 'Delete' && start < 4 && start === end)
        ) {
          e.preventDefault();
          return;
        }
      }
      onKeyDown?.(e);
    }

    return (
      <Input
        ref={mergedRef}
        type="text"
        inputMode={maskType === 'phone_ch' ? 'tel' : maskType === 'iban_ch' ? 'text' : 'numeric'}
        placeholder={placeholder ?? MASK_PLACEHOLDERS[maskType]}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        {...props}
      />
    );
  }
);
